import { useState, useRef, useEffect } from 'preact/hooks';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Brain, 
  Play, 
  Loader2, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  FileText,
  Code,
  GitBranch,
  Target,
  Lightbulb,
  BarChart3,
  Terminal
} from 'lucide-preact';

interface Project {
  id: string;
  name: string;
  type: 'zip' | 'git';
  description?: string;
  path: string;
  createdAt: string;
  updatedAt: string;
  originalFile?: string;
  isActive: boolean;
}

interface TodoItem {
  id: string;
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
  category?: string;
  priority?: 'low' | 'medium' | 'high';
}

interface AnalysisResult {
  success: boolean;
  output: string;
  workingDirectory: string;
  command: string;
  error?: string;
}

interface ProjectAnalystProps {
  project: Project;
  onClose: () => void;
}

const ANALYSIS_TEMPLATES = [
  {
    id: 'overview',
    title: 'Project Overview',
    description: 'Get a comprehensive analysis of the project structure and architecture',
    icon: FileText,
    prompt: 'Analyze this project and provide a comprehensive overview including:\n1. Project structure and main components\n2. Technology stack and dependencies\n3. Architecture patterns used\n4. Key files and their purposes\n5. Potential improvements and recommendations\n\nCreate a detailed todo list for any improvements identified.'
  },
  {
    id: 'architecture',
    title: 'Architecture Review',
    description: 'Review the architectural patterns and design decisions',
    icon: Code,
    prompt: 'Conduct a detailed architecture review of this project:\n1. Identify architectural patterns and design decisions\n2. Analyze component relationships and dependencies\n3. Assess scalability and maintainability\n4. Identify potential architectural improvements\n5. Suggest refactoring opportunities\n\nCreate a prioritized todo list for architectural improvements.'
  },
  {
    id: 'code-quality',
    title: 'Code Quality Analysis',
    description: 'Analyze code quality, patterns, and technical debt',
    icon: BarChart3,
    prompt: 'Perform a comprehensive code quality analysis:\n1. Identify code patterns and anti-patterns\n2. Assess code organization and structure\n3. Look for potential bugs and issues\n4. Evaluate testing coverage and quality\n5. Identify technical debt and improvement opportunities\n\nCreate a detailed todo list for code quality improvements.'
  },
  {
    id: 'feature-planning',
    title: 'Feature Planning',
    description: 'Plan new features and development roadmap',
    icon: Target,
    prompt: 'Help plan new features for this project:\n1. Analyze current functionality and gaps\n2. Suggest new features based on project type\n3. Create implementation roadmaps\n4. Identify dependencies and prerequisites\n5. Estimate complexity and effort\n\nCreate a comprehensive todo list for feature development.'
  },
  {
    id: 'security-review',
    title: 'Security Review',
    description: 'Review security practices and identify vulnerabilities',
    icon: AlertCircle,
    prompt: 'Conduct a security review of this project:\n1. Identify potential security vulnerabilities\n2. Review authentication and authorization\n3. Check for sensitive data exposure\n4. Assess input validation and sanitization\n5. Recommend security improvements\n\nCreate a prioritized todo list for security fixes.'
  },
  {
    id: 'performance',
    title: 'Performance Analysis',
    description: 'Analyze performance bottlenecks and optimization opportunities',
    icon: Lightbulb,
    prompt: 'Analyze the performance of this project:\n1. Identify performance bottlenecks\n2. Review resource usage patterns\n3. Suggest optimization strategies\n4. Assess scalability concerns\n5. Recommend performance improvements\n\nCreate a detailed todo list for performance optimizations.'
  }
];

export function ProjectAnalyst({ project, onClose }: ProjectAnalystProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [analysisHistory, setAnalysisHistory] = useState<string[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  const handleAnalysis = async (prompt: string) => {
    // Analysis is now handled by the persistent terminal
    setAnalysisHistory(prev => [...prev, `Analysis: ${prompt}`]);
    console.log('Analysis request:', prompt);
  };

  const parseTodosFromOutput = (output: string) => {
    // Simple todo parsing - look for common todo patterns
    const todoLines = output.split('\n').filter(line => 
      line.includes('TODO') || 
      line.includes('todo') || 
      line.includes('•') || 
      line.includes('- [ ]') ||
      line.includes('- [x]')
    );
    
    const parsedTodos: TodoItem[] = todoLines.map((line, index) => ({
      id: `todo-${index}`,
      content: line.replace(/^[-•]\s*\[[ x]\]\s*/, '').trim(),
      status: line.includes('[x]') ? 'completed' : 'pending',
      category: 'Analysis',
      priority: 'medium'
    }));
    
    setTodos(parsedTodos);
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = ANALYSIS_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setCustomPrompt(template.prompt);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-lg font-semibold">Project Analyst</h2>
            <p className="text-sm text-muted-foreground">
              AI-powered analysis for {project.name}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>

      {/* Analysis Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Analysis Templates</CardTitle>
          <CardDescription>
            Choose a pre-defined analysis or create a custom prompt
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ANALYSIS_TEMPLATES.map((template) => {
              const IconComponent = template.icon;
              return (
                <Card 
                  key={template.id}
                  className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                    selectedTemplate === template.id ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <IconComponent className="h-5 w-5 text-primary mt-0.5" />
                      <div className="flex-1">
                        <h3 className="font-medium text-sm">{template.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {template.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Custom Prompt */}
      <Card>
        <CardHeader>
          <CardTitle>Analysis Prompt</CardTitle>
          <CardDescription>
            Customize the analysis prompt or use a template
          </CardDescription>
        </CardHeader>
        <CardContent>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt((e.target as HTMLTextAreaElement).value)}
            placeholder="Enter your analysis prompt here..."
            className="w-full h-32 p-3 border rounded-md resize-none"
          />
          <div className="flex justify-end mt-3">
            <Button 
              onClick={() => handleAnalysis(customPrompt)}
              disabled={!customPrompt.trim() || isAnalyzing}
              className="gap-2"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run Analysis
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Terminal Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            Use the Persistent Terminal
          </CardTitle>
          <CardDescription>
            The terminal at the bottom of the page is your main interface for Byte DevOps
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
              <div>
                <p className="text-sm font-medium">Terminal is Always Available</p>
                <p className="text-xs text-muted-foreground">
                  Use the terminal at the bottom of the page to interact with Byte DevOps
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
              <div>
                <p className="text-sm font-medium">Project Context</p>
                <p className="text-xs text-muted-foreground">
                  The terminal automatically works within your selected project directory
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-purple-500 mt-2" />
              <div>
                <p className="text-sm font-medium">Quick Actions</p>
                <p className="text-xs text-muted-foreground">
                  Use the quick action buttons in the terminal sidebar for common tasks
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analysis History */}
      {analysisHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Analysis History</CardTitle>
            <CardDescription>
              Previous analysis requests for this session
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analysisHistory.map((item, index) => (
                <div key={index} className="text-sm text-muted-foreground p-2 bg-muted rounded">
                  {item}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Todo List */}
      {todos.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Development Todos
            </CardTitle>
            <CardDescription>
              Tasks identified during analysis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {todos.map((todo) => (
                <div key={todo.id} className="flex items-start gap-3 p-3 border rounded-md">
                  {getStatusIcon(todo.status)}
                  <div className="flex-1">
                    <p className="text-sm">{todo.content}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={getPriorityColor(todo.priority)} className="text-xs">
                        {todo.priority || 'medium'}
                      </Badge>
                      {todo.category && (
                        <Badge variant="outline" className="text-xs">
                          {todo.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
